import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

// ─── PRIVACY POLICY ───────────────────────────────────────────────────────────
export function PrivacyPolicy() {
  const sections = [
    {
      title: '1. Information We Collect',
      content: `We collect information you provide directly when you:
      • Fill out our contact or quote request forms (name, email, phone, address)
      • Apply for a job (resume, work history, personal details)
      • Subscribe to our newsletter or follow us on social media
      • Communicate with us via WhatsApp, phone or email

      We may also collect non-personal data such as browser type, device type, pages visited and time spent on our website through standard analytics tools.`,
    },
    {
      title: '2. How We Use Your Information',
      content: `We use the information collected to:
      • Respond to your enquiries and provide interior design quotes
      • Schedule and manage design consultations and site visits
      • Send you project updates and relevant communications
      • Improve our website, services and customer experience
      • Comply with legal obligations and protect our business interests
      • Send promotional material (only with your consent)`,
    },
    {
      title: '3. Information Sharing',
      content: `We do not sell, trade or rent your personal information to third parties. We may share your information only with:
      • Service providers who assist our operations (e.g. email service, cloud hosting)
      • Professional advisors (lawyers, accountants) bound by confidentiality
      • Authorities when required by law

      All third parties are required to maintain the confidentiality of your information.`,
    },
    {
      title: '4. Data Security',
      content: `We implement appropriate technical and organisational measures to protect your personal data, including SSL encryption, secure servers and restricted staff access. However, no method of internet transmission or electronic storage is 100% secure. We encourage you not to share sensitive financial information over unsecured channels.`,
    },
    {
      title: '5. Cookies',
      content: `Our website uses cookies to enhance your browsing experience, remember your preferences and analyse site traffic. You can control cookies through your browser settings. Disabling cookies may affect some features of our website. We use Google Analytics which may set its own cookies — you can opt out via Google's opt-out tool.`,
    },
    {
      title: '6. Third-Party Links',
      content: `Our website may contain links to third-party websites (e.g. social media, Google Maps). We are not responsible for the privacy practices or content of these sites. We encourage you to read the privacy policies of any third-party sites you visit.`,
    },
    {
      title: '7. Your Rights',
      content: `You have the right to:
      • Access the personal information we hold about you
      • Correct inaccurate or incomplete data
      • Request deletion of your personal data
      • Withdraw consent for marketing communications at any time
      • Lodge a complaint with a supervisory authority

      To exercise these rights, contact us at hello@wedoointeriors.com.`,
    },
    {
      title: '8. Children\'s Privacy',
      content: `Our services are not directed to children under 18 years of age. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a minor, please contact us immediately.`,
    },
    {
      title: '9. Changes to This Policy',
      content: `We may update this Privacy Policy from time to time. We will notify you of significant changes by posting a notice on our website. Your continued use of our services after changes are posted constitutes your acceptance of the revised policy.`,
    },
    {
      title: '10. Contact Us',
      content: `For any privacy-related questions, concerns or requests:\n\nWedoo Interiors\n #1,Near Muneshwara Temple, Manjunath Nagar, Gokula Extension\nTumakuru, Karnataka 572104\n\nEmail: hello@wedoointeriors.com\nPhone: +91 96865 83195`,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Wedoo Interiors</title>
        <meta name="description" content="Privacy Policy for Wedoo Interiors — how we collect, use and protect your personal information." />
        <link rel="canonical" href="https://wedoointeriors.com/privacy-policy" />
      </Helmet>
      <div style={{ paddingTop: '6rem', paddingBottom: '4rem', background: 'var(--ink-2)', borderBottom: '1px solid rgba(245,168,0,0.12)' }}>
        <div className="container">
          <span className="tag-label" style={{ marginBottom: '1rem', display: 'flex' }}>Legal</span>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2rem,4vw,3rem)', fontWeight:600, color: 'var(--cream)' }}>
            Privacy <em style={{ color: 'var(--gold)' }}>Policy</em>
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginTop: '1rem' }}>
            Last updated: {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>
      <section className="section-pad">
        <div className="container" style={{ maxWidth: '800px' }}>
          <p style={{ color: 'var(--cream-2)', fontSize: '0.92rem', lineHeight: '1.9', marginBottom: '3rem', padding: '1.5rem', background: 'var(--ink-2)', borderLeft: '3px solid var(--gold)' }}>
            Wedoo Interiors ("we", "our", "us") is committed to protecting your privacy. This policy explains how we collect, use, disclose and safeguard your personal information when you visit our website or use our services.
          </p>
          {sections.map((s, i) => (
            <div key={i} style={{ marginBottom: '2.5rem' }}>
              <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.35rem', fontWeight: 400, color: 'var(--cream)', marginBottom: '1rem' }}>{s.title}</h2>
              <div className="gold-line" style={{ width: '32px', marginBottom: '1rem' }} />
              <p style={{ color: 'var(--cream-2)', fontSize: '0.88rem', lineHeight: '1.9', whiteSpace: 'pre-line' }}>{s.content}</p>
            </div>
          ))}
          <div style={{ marginTop: '3rem', padding: '2rem', background: 'var(--ink-2)', border: '1px solid var(--ink-4)', borderRadius:'var(--radius)', textAlign: 'center' }}>
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>Have a privacy concern? We're here to help.</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn btn-gold" style={{ padding: '0.7rem 1.5rem', fontSize: '0.7rem' }}>Contact Us</Link>
              <Link to="/terms-and-conditions" className="btn btn-outline" style={{ padding: '0.7rem 1.5rem', fontSize: '0.7rem' }}>Terms & Conditions</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ─── TERMS & CONDITIONS ───────────────────────────────────────────────────────
export function TermsAndConditions() {
  const sections = [
    { title: '1. Acceptance of Terms', content: 'By accessing and using the Wedoo Interiors website and services, you accept and agree to be bound by these Terms and Conditions. If you do not agree, please do not use our services. These terms apply to all visitors, clients, and users of our website and interior design services.' },
    { title: '2. Services', content: 'Wedoo Interiors provides interior design consultancy, modular furniture supply, site execution, and related services. Service scope, deliverables, and timelines are defined in the individual project agreement signed with each client. We reserve the right to refuse service at our discretion.' },
    { title: '3. Quotations & Pricing', content: 'All quotations are valid for 30 days from the date of issue. Final pricing is confirmed only after a site visit and signed project agreement. Material costs are subject to market fluctuations and may be revised if the project start date is delayed beyond 60 days from quotation.' },
    { title: '4. Payment Terms', content: 'Payments are structured in three stages: 5% on design sign-off, 50% on production dispatch, and 45% on completion. Delayed payments may result in project delays. We reserve the right to pause work if payment milestones are not met within 7 days of the due date.' },
    { title: '5. Design Ownership', content: 'All design concepts, drawings, 3D renders and presentations created by Wedoo Interiors remain our intellectual property until full payment is received. Upon complete payment, the client receives rights to use the designs for their personal space only. Designs may not be shared, replicated or sold to third parties.' },
    { title: '6. Project Timeline', content: 'Timelines agreed upon in the project contract are estimates based on standard conditions. Wedoo Interiors will communicate proactively if timelines are affected by material shortages, site access issues, change orders or events outside our control. Change orders requested by the client will be quoted and may affect the timeline.' },
    { title: '7. Warranty', content: 'We provide a 5-year warranty on modular furniture and woodwork against manufacturing defects. This warranty does not cover damage caused by misuse, accidents, water damage, or modifications made by third parties. Civil work warranty (false ceiling, painting) is 1 year from handover date. Warranty claims must be reported in writing within 7 days of discovering the defect.' },
    { title: '8. Limitation of Liability', content: 'Wedoo Interiors shall not be liable for any indirect, incidental or consequential damages arising from our services. Our maximum liability is limited to the total amount paid by the client for the specific project in question. We are not responsible for pre-existing structural issues, building society restrictions, or force majeure events.' },
    { title: '9. Cancellation Policy', content: 'Cancellations after design sign-off will forfeit the initial 5% payment. Cancellations after production has begun will forfeit up to 50% of the project value to cover materials and labour already invested. Wedoo Interiors reserves the right to cancel projects with a full refund if we cannot execute the work to our quality standards.' },
    { title: '10. Governing Law', content: 'These Terms and Conditions are governed by the laws of India, specifically the jurisdiction of Tumakuru District, Karnataka. Any disputes shall first be attempted to be resolved amicably. If unresolved, disputes will be subject to the exclusive jurisdiction of courts in Tumakuru, Karnataka.' },
  ];

  return (
    <>
      <Helmet>
        <title>Terms & Conditions | Wedoo Interiors</title>
        <meta name="description" content="Terms and Conditions for Wedoo Interiors services — payment, warranty, design ownership and project policies." />
        <link rel="canonical" href="https://wedoointeriors.com/terms-and-conditions" />
      </Helmet>
      <div style={{ paddingTop: '6rem', paddingBottom: '4rem', background: 'var(--ink-2)', borderBottom: '1px solid rgba(245,168,0,0.12)' }}>
        <div className="container">
          <span className="tag-label" style={{ marginBottom: '1rem', display: 'flex' }}>Legal</span>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2rem,4vw,3rem)', fontWeight:600, color: 'var(--cream)' }}>
            Terms & <em style={{ color: 'var(--gold)' }}>Conditions</em>
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginTop: '1rem' }}>
            Last updated: {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>
      <section className="section-pad">
        <div className="container" style={{ maxWidth: '800px' }}>
          {sections.map((s, i) => (
            <div key={i} style={{ marginBottom: '2.5rem', paddingBottom: '2.5rem', borderBottom: i < sections.length - 1 ? '1px solid var(--ink-5)' : 'none' }}>
              <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.35rem', fontWeight: 400, color: 'var(--cream)', marginBottom: '1rem' }}>{s.title}</h2>
              <p style={{ color: 'var(--cream-2)', fontSize: '0.88rem', lineHeight: '1.9' }}>{s.content}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

// ─── SITEMAP ──────────────────────────────────────────────────────────────────
export function Sitemap() {
  const sections = [
    {
      title: 'Main Pages',
      links: [
        { label: 'Home', path: '/', desc: 'Hero, services overview, portfolio preview, social feed' },
        { label: 'About Us', path: '/about', desc: 'Our story, team, values and studio address' },
        { label: 'Services', path: '/services', desc: 'Overview of all interior design services' },
        { label: 'Portfolio', path: '/portfolio', desc: 'Gallery of completed interior projects' },
        { label: 'Careers', path: '/careers', desc: 'Job openings and application form' },
        { label: 'Contact', path: '/contact', desc: 'Contact form, address and Google Maps' },
        { label: 'Get Free Quote', path: '/get-free-quote', desc: 'Request a personalised design quotation' },
      ],
    },
    {
      title: 'Service Pages',
      links: [
        { label: 'Complete Modular Works', path: '/services/complete-modular-site-work', desc: 'Factory-built modular units and complete site execution' },
        { label: 'Interior Design', path: '/services/interior-design', desc: '3D design, space planning and full interior execution' },
        { label: 'Home Lighting', path: '/services/home-lighting', desc: 'Ambient, task and accent lighting design' },
        { label: 'Modular Kitchen', path: '/services/modular-kitchen', desc: 'Custom modular kitchens with premium fittings' },
        { label: 'False Ceiling & Painting', path: '/services/false-ceiling-painting', desc: 'Gypsum, POP ceilings and premium wall finishes' },
        { label: 'Aluminium & Glass Works', path: '/services/aluminium-glass-works', desc: 'Glass partitions, shower enclosures, aluminium doors' },
      ],
    },
    {
      title: 'Knowledge & Resources',
      links: [
        { label: 'Blog & Knowledge Hub', path: '/blog', desc: 'Interior design tips, trends and home décor guides' },
        { label: 'Client Reviews', path: '/reviews', desc: 'Verified Google reviews and client testimonials' },
        { label: 'FAQ', path: '/faq', desc: 'Common questions about pricing, process and warranty' },
        { label: 'Knowledge Hub', path: '/knowledge-hub', desc: 'Expert interior design guides and ideas' },
      ],
    },
    {
      title: 'CRM & Tools',
      links: [
        { label: 'Get Free Quote', path: '/get-free-quote', desc: 'Request a personalised interior design quotation' },
        { label: 'Careers', path: '/careers', desc: 'Job openings at Wedoo Interiors, Tumakuru' },
        { label: 'Contact Us', path: '/contact', desc: 'Visit our studio or call us' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Privacy Policy', path: '/privacy-policy', desc: 'How we collect and protect your information' },
        { label: 'Terms & Conditions', path: '/terms-and-conditions', desc: 'Project terms, payment and warranty policies' },
        { label: 'Sitemap', path: '/sitemap', desc: 'Complete list of all website pages' },
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Sitemap | Wedoo Interiors</title>
        <meta name="description" content="Complete sitemap for Wedoo Interiors website — find all pages including services, blog, portfolio and legal pages." />
        <link rel="canonical" href="https://wedoointeriors.com/sitemap" />
      </Helmet>
      <div style={{ paddingTop: '6rem', paddingBottom: '4rem', background: 'var(--ink-2)', borderBottom: '1px solid rgba(245,168,0,0.12)' }}>
        <div className="container">
          <span className="tag-label" style={{ marginBottom: '1rem', display: 'flex' }}>Navigation</span>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2rem,4vw,3rem)', fontWeight:600, color: 'var(--cream)' }}>
            Site<em style={{ color: 'var(--gold)' }}>map</em>
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: '0.75rem' }}>Complete guide to all pages on the Wedoo Interiors website.</p>
        </div>
      </div>
      <section className="section-pad">
        <div className="container">
          <div className="grid-2" style={{ gap: '3rem' }}>
            {sections.map((section) => (
              <div key={section.title}>
                <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.35rem', fontWeight: 400, color: 'var(--cream)', marginBottom: '0.5rem' }}>{section.title}</h2>
                <div className="gold-line" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                  {section.links.map(link => (
                    <Link key={link.path} to={link.path} style={{
                      display: 'block', padding: '1rem 0',
                      borderBottom: '1px solid var(--ink-5)',
                      textDecoration: 'none', transition: 'all 0.3s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.paddingLeft = '0.75rem'}
                      onMouseLeave={e => e.currentTarget.style.paddingLeft = '0'}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: '0.88rem', color: 'var(--cream)', fontWeight: '500', marginBottom: '0.25rem' }}>{link.label}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{link.desc}</div>
                        </div>
                        <span style={{ color: 'var(--gold)', fontSize: '0.8rem', opacity: 0.6 }}>→</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
